import React from "react";

import { Minus, TrendingDown, TrendingUp } from "lucide-react";

import { formatCompactCurrency } from "../../../../../Utils/Helper/formatCompactCurrency";

type ClusterId = "renewal-risk" | "strong-performers" | "high-adoption-mixed";
type Quadrant = "expand" | "triage" | "defend" | "delegate";

export type MapHoverPoint = {
  account_id: string;
  account_name: string;
  cluster: ClusterId;
  quadrant: Quadrant;
  overall: number;
  execEngagement: number;
  reachOpp: number;
  reachRisk: number;
  arrM: number;
  nrr: number;
};

const CLUSTER_META: Record<
  ClusterId,
  { label: string; color: string; bg: string; text: string }
> = {
  "renewal-risk": {
    label: "Renewal Risk",
    color: "#ef4444",
    bg: "#fee2e2",
    text: "#dc2626",
  },
  "strong-performers": {
    label: "Strong Performers",
    color: "#22c55e",
    bg: "#dcfce7",
    text: "#16a34a",
  },
  "high-adoption-mixed": {
    label: "High Adoption / Mixed",
    color: "#eab308",
    bg: "#fefce8",
    text: "#eab308",
  },
};

const QUADRANT_META: Record<
  Quadrant,
  { label: string; color: string; bg: string; text: string }
> = {
  triage: { label: "Triage", color: "#ef4444", bg: "#fee2e2", text: "#dc2626" },
  defend: { label: "Defend", color: "#f97316", bg: "#ffedd5", text: "#ea580c" },
  delegate: {
    label: "Monitor",
    color: "#94a3b8",
    bg: "#f1f5f9",
    text: "#64748b",
  },
  expand: { label: "Expand", color: "#22c55e", bg: "#dcfce7", text: "#16a34a" },
};

const formatArrRevenueBand = (arrM: number): string => {
  if (!Number.isFinite(arrM) || arrM <= 0) return "—";
  if (arrM < 0.5) return "<$0.5M";
  if (arrM < 1) return "<$1M";
  return formatCompactCurrency(arrM, { inputScale: "millions", fallback: "—" });
};

const formatReachAxis = (value: number): string => {
  if (!Number.isFinite(value)) return "—";
  return `${Math.round(value * 100)}%`;
};

const scoreTrajectoryLabel = (overall: number): string => {
  if (overall >= 75) return "Improving";
  if (overall >= 50) return "Stable";
  return "Declining";
};

const clusterInsightLine = (cluster: ClusterId): string => {
  switch (cluster) {
    case "renewal-risk":
      return "Renewal exposure elevated — align exec sponsor and renewal playbook.";
    case "strong-performers":
      return "Strong trajectory — prioritize expansion and advocacy.";
    default:
      return "Mixed signals — monitor adoption and executive engagement.";
  }
};

const execAttentionSummary = (
  exec: number,
  caution: number,
  critical: number
): string => {
  const e = Math.round(exec);
  if (exec < critical) {
    return `Exec ${e} — critical, immediate coverage`;
  }
  if (exec < caution) {
    return `Exec ${e} — low, strengthen coverage`;
  }
  return `Exec ${e} — healthy coverage`;
};

const getExecAttentionTier = (
  eng: number,
  caution: number,
  critical: number
): "critical" | "caution" | "none" => {
  if (eng < critical) return "critical";
  if (eng < caution) return "caution";
  return "none";
};

function ScoreTrendIcon({ overall }: { overall: number }) {
  if (overall >= 75) {
    return (
      <TrendingUp
        className="h-3.5 w-3.5 shrink-0 text-emerald-600"
        strokeWidth={2.5}
        aria-hidden
      />
    );
  }
  if (overall >= 50) {
    return (
      <Minus
        className="h-3.5 w-3.5 shrink-0 text-amber-600"
        strokeWidth={2.5}
        aria-hidden
      />
    );
  }
  return (
    <TrendingDown
      className="h-3.5 w-3.5 shrink-0 text-red-600"
      strokeWidth={2.5}
      aria-hidden
    />
  );
}

export function ReachMapAccountHoverCard({
  point,
  execAttnCaution,
  execAttnCritical,
}: {
  point: MapHoverPoint;
  execAttnCaution: number;
  execAttnCritical: number;
}) {
  const clusterMeta = CLUSTER_META[point.cluster];
  const quadrantMeta = QUADRANT_META[point.quadrant];
  const execLine = execAttentionSummary(
    point.execEngagement,
    execAttnCaution,
    execAttnCritical
  );
  const tier = getExecAttentionTier(
    point.execEngagement,
    execAttnCaution,
    execAttnCritical
  );

  return (
    <div
      className="w-[min(100vw-16px,268px)] space-y-2 rounded-lg border border-slate-200 bg-white p-2.5 shadow-xl"
      onPointerDown={e => e.stopPropagation()}
    >
      <h3 className="mb-0 text-sm font-bold leading-snug text-slate-900">
        {point.account_name}
      </h3>
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-nowrap items-center gap-1.5">
          <span className="inline-flex shrink-0 items-center rounded-full bg-orange-100 px-2 py-px text-[11px] font-bold text-orange-900">
            Account Health: {Math.round(point.overall)}
          </span>
          <span
            className="inline-flex shrink-0 items-center rounded-full border px-2 py-px text-[11px] font-semibold"
            style={{
              background: quadrantMeta.bg,
              color: quadrantMeta.text,
              borderColor: `${quadrantMeta.color}55`,
            }}
          >
            {quadrantMeta.label}
          </span>
        </div>
        <span
          className="inline-flex w-fit max-w-full items-center rounded-full px-2 py-px text-[11px] font-semibold"
          style={{
            background: clusterMeta.bg,
            color: clusterMeta.text,
            border: `1px solid ${clusterMeta.color}44`,
          }}
        >
          {clusterMeta.label}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1.5 text-[11px]">
        <div className="rounded-md bg-slate-50 px-2 py-1.5">
          <p className="mb-px text-[9px] font-medium uppercase tracking-wide text-slate-500">
            Risk
          </p>
          <p className="mb-0 font-bold text-slate-900">
            {formatReachAxis(point.reachRisk)}
          </p>
        </div>
        <div className="rounded-md bg-slate-50 px-2 py-1.5">
          <p className="mb-px text-[9px] font-medium uppercase tracking-wide text-slate-500">
            Growth opportunity
          </p>
          <p className="mb-0 font-bold text-slate-900">
            {formatReachAxis(point.reachOpp)}
          </p>
        </div>
      </div>
      <div className="flex items-start gap-1.5 text-xs leading-snug">
        <span
          className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full"
          style={{
            backgroundColor:
              tier === "critical"
                ? "#dc2626"
                : tier === "caution"
                  ? "#d97706"
                  : "#22c55e",
          }}
          aria-hidden
        />
        {tier === "critical" ? (
          <span className="text-base font-black leading-none text-red-600">
            !
          </span>
        ) : (
          <span className="text-base leading-none text-orange-600">▲</span>
        )}
        <span className="text-slate-700">{execLine}</span>
      </div>
      <div className="border-l-2 border-slate-200 pl-2 text-xs leading-relaxed text-slate-600">
        {clusterInsightLine(point.cluster)}
      </div>
    </div>
  );
}

export function RevenueMapAccountHoverCard({
  point,
}: {
  point: MapHoverPoint;
}) {
  const clusterMeta = CLUSTER_META[point.cluster];
  const arrLabel = formatArrRevenueBand(point.arrM);
  const nrrNum = Number.isFinite(point.nrr) ? Math.round(point.nrr) : null;
  const nrrStrong = nrrNum !== null && nrrNum >= 100;
  const trajectory = scoreTrajectoryLabel(point.overall);
  const trendColor =
    point.overall >= 75
      ? "text-emerald-700"
      : point.overall >= 50
        ? "text-amber-700"
        : "text-red-700";

  return (
    <div
      className="w-[min(100vw-16px,268px)] space-y-2 rounded-lg border border-slate-200 bg-white p-2.5 shadow-xl"
      onPointerDown={e => e.stopPropagation()}
    >
      <h3 className="mb-0 text-sm font-bold leading-snug text-slate-900">
        {point.account_name}
      </h3>
      <div className="grid grid-cols-3 gap-1.5">
        <div className="rounded-md bg-slate-100 px-1.5 py-1.5">
          <p className="mb-px text-[9px] font-medium uppercase tracking-wide text-slate-500">
            ARR
          </p>
          <p className="mb-0 text-xs font-bold leading-tight text-slate-900">
            {arrLabel}
          </p>
        </div>
        <div className="rounded-md bg-slate-100 px-1.5 py-1.5">
          <p className="mb-px text-[9px] font-medium uppercase tracking-wide text-slate-500">
            NRR
          </p>
          <p
            className={`mb-0 text-xs font-bold leading-tight ${
              nrrStrong ? "text-emerald-600" : "text-slate-900"
            }`}
          >
            {nrrNum !== null ? `${nrrNum}%` : "—"}
          </p>
        </div>
        <div
          className="rounded-md px-1.5 py-1.5"
          style={{ background: clusterMeta.bg }}
        >
          <p
            className="mb-px text-[9px] font-medium uppercase tracking-wide opacity-90"
            style={{ color: clusterMeta.text }}
          >
            Account Health
          </p>
          <p
            className="mb-0 text-xs font-bold leading-tight"
            style={{ color: clusterMeta.text }}
          >
            {Math.round(point.overall)}
          </p>
        </div>
      </div>
      <div
        className={`flex flex-wrap items-center gap-1 text-[11px] font-semibold leading-none ${trendColor}`}
      >
        <ScoreTrendIcon overall={point.overall} />
        <span>{trajectory}</span>
        <span className="font-normal text-slate-400">·</span>
        <span
          className="inline-flex items-center rounded-full px-1.5 py-px text-[10px] font-semibold"
          style={{
            background: clusterMeta.bg,
            color: clusterMeta.text,
          }}
        >
          {clusterMeta.label}
        </span>
      </div>
    </div>
  );
}

export const HOVER_CARD_WIDTH = 268;
export const HOVER_CARD_GAP = 12;

export function computeMapHoverPosition(
  clientX: number,
  clientY: number,
  cardHeight = 200
): { left: number; top: number } {
  const pad = HOVER_CARD_GAP;
  let left = clientX + pad;
  let top = clientY + pad;
  if (left + HOVER_CARD_WIDTH > window.innerWidth - pad) {
    left = clientX - HOVER_CARD_WIDTH - pad;
  }
  if (top + cardHeight > window.innerHeight - pad) {
    top = clientY - cardHeight - pad;
  }
  return {
    left: Math.max(pad, left),
    top: Math.max(pad, top),
  };
}
